import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { CartProvider } from "@/lib/CartContext";
import { SettingsProvider } from "@/lib/SettingsContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppBubble from "@/components/WhatsAppBubble";
import { Loader2 } from "lucide-react";

import LandingPage from "@/pages/LandingPage";
import ShopPage from "@/pages/ShopPage";
import ProductPage from "@/pages/ProductPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";
import ProfilePage from "@/pages/ProfilePage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import PublicOrderView from "@/pages/PublicOrderView";
import AdminDashboard from "@/pages/AdminDashboard";
import ProductionBoard from "@/pages/ProductionBoard";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import HowItWorksPage from "@/pages/HowItWorksPage";
import ContactPage from "@/pages/ContactPage";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth callback via URL fragment
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isPublicOrderRoute = location.pathname.startsWith("/mi-pedido");
  const hideChrome = isAdminRoute || isPublicOrderRoute;
  return (
    <>
      {!hideChrome && <Navbar />}
      <main className={hideChrome ? "" : "min-h-[60vh]"}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/tienda" element={<ShopPage />} />
          <Route path="/producto/:productId" element={<ProductPage />} />
          <Route path="/carrito" element={<CartPage />} />
          <Route path="/como-funciona" element={<HowItWorksPage />} />
          <Route path="/contacto" element={<ContactPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/registro" element={<RegisterPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/perfil" element={<Protected><ProfilePage /></Protected>} />
          <Route path="/pago/exito" element={<PaymentSuccess />} />
          <Route path="/pago/cancelado" element={<Protected><PaymentCancel /></Protected>} />
          <Route path="/pedido/:orderId" element={<OrderDetailPage />} />
          <Route path="/mi-pedido/:orderNumber" element={<PublicOrderView />} />
          <Route path="/admin" element={<Protected roles={["admin", "operator"]}><AdminDashboard /></Protected>} />
          <Route path="/admin/produccion" element={<Protected roles={["admin", "operator"]}><ProductionBoard /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!hideChrome && <Footer />}
      {!hideChrome && <WhatsAppBubble />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SettingsProvider>
            <CartProvider>
              <AppRouter />
              <Toaster position="top-right" theme="light" richColors />
            </CartProvider>
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
